import { useEffect, useRef, type ReactNode } from 'react'
import { initDetails } from '../../lib/kitDetails'

/**
 * The kit's module 10 card: a short claim that opens in place rather than into a modal, so nobody
 * loses their scroll position to read one sentence.
 *
 * React owns the markup; `lib/kitDetails.ts` owns the open and close animation. It stays a real
 * `<details>`, so with no script at all it is still a working disclosure — which is the whole
 * reason the kit built it this way.
 *
 * The review's fourth fix lives in the stylesheet rather than here: the grid needs
 * `align-items: start`, or opening one card stretches every neighbour in its row to match, which
 * is the exact opposite of what the module is for.
 */
export default function ExpandCard({
  title,
  children,
  delay,
}: {
  title: string
  children: ReactNode
  /** Reveal stagger step, read by the stylesheet as `--d`. */
  delay?: number
}) {
  const ref = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    return initDetails(el)
  }, [])

  return (
    <details
      className="xcard"
      ref={ref}
      data-reveal
      style={delay ? ({ '--d': delay } as React.CSSProperties) : undefined}
    >
      <summary>
        <h3>
          {title}
          <span className="xplus" aria-hidden="true" />
        </h3>
      </summary>
      <div className="xbody">{children}</div>
    </details>
  )
}
